import {useMemo} from 'react';
import {useQuery} from '@tanstack/react-query';
import {getUserOrganizationsPlain, getUserUniqueSignDocsAPI, getAllAdminSignDocsAPI, getAdminCreatedSignDocTypesAPI} from '../../Services/api';
import {getAdminOrganizations} from '../../Utils/User/UserLib';
import {queryClient} from '../../Utils/queryClient';
import {orderBy} from 'lodash';

// Pre-compute all types/categories once at module load (stable references)
import { getAllSignDocTypes } from '../SignDoc/SignDocLib';
import { getAllSignDocCategories } from '../../Utils/Constants';
const ALL_SIGN_DOC_TYPES = getAllSignDocTypes();
const ALL_SIGN_DOC_CATEGORIES = getAllSignDocCategories();

/**
 * Filters SignDocs array by types and/or categories
 * Shared helper to prevent code duplication between hook and function
 *
 * @param {object} params - Function parameters
 * @param {Array} params.signDocs - Array of SignDocs to filter
 * @param {Array<string>} [params.types] - SignDoc types to filter by
 * @param {Array<string>} [params.categories] - SignDoc categories to filter by
 * @returns {Array} Filtered array of SignDocs
 */
const filterSignDocs = ({signDocs, types, categories}) => {
    let filtered = signDocs;

    if (types && types.length > 0) {
        filtered = filtered.filter(doc => types.includes(doc.type));
    }

    if (categories && categories.length > 0) {
        filtered = filtered.filter(doc => categories.includes(doc.category));
    }

    return filtered;
};

/**
 * Centralized query keys for React Query
 *
 * Used to identify cached data and invalidate cache when data changes.
 */
export const queryKeys = {
    adminOrganizations: () => ['adminOrganizations'],
    uniqueSignDocs: (includePersonalOrgs = false) => ['uniqueSignDocs', {includePersonalOrgs}],
    allAdminSignDocs: (showArchived = false) => ['allAdminSignDocs', {showArchived}],
    adminCreatedSignDocTypes: (showArchived = false) => ['adminCreatedSignDocTypes', {showArchived}],
};

/**
 * Invalidates React Query cache for given query keys.
 * Accepts a single query key or an array of query keys (normalized internally).
 *
 * @param {object} params - Function parameters
 * @param {Array|Array<Array>} params.queryKeys - Single query key or array of query keys to invalidate
 *
 * @example
 * // Single key
 * invalidateQueries({queryKeys: queryKeys.adminOrganizations()});
 *
 * // Multiple keys
 * invalidateQueries({queryKeys: [queryKeys.adminOrganizations(), queryKeys.uniqueSignDocs()]});
 */
export const invalidateQueries = ({queryKeys: queryKeysToInvalidate}) => {
    if (!queryKeysToInvalidate) {
        console.warn('invalidateQueries called without queryKeys - no cache invalidated');
        return;
    }

    const keysArray = Array.isArray(queryKeysToInvalidate) ? queryKeysToInvalidate : [queryKeysToInvalidate];

    keysArray.forEach(queryKey => {
        queryClient.invalidateQueries({queryKey});
    });
};

/**
 * Fetches all admin SignDocs using React Query cache
 *
 * Use this in utility functions (non-React code) to access the same cache
 * as useAllAdminSignDocsQuery. Returns cached data if fresh, fetches if stale.
 *
 * @param {object} params - Function parameters
 * @param {Array<string>} [params.types] - SignDoc types to filter (frontend filter)
 * @param {Array<string>} [params.categories] - SignDoc categories to filter (frontend filter)
 * @param {boolean} [params.showArchived=false] - Whether to include archived SignDocs
 * @returns {Promise<Array>} Array of admin SignDocs (filtered if types/categories provided)
 *
 * @example
 * // Get all SignDocs
 * const allSignDocs = await fetchAllAdminSignDocs();
 *
 * // Get filtered SignDocs
 * const filtered = await fetchAllAdminSignDocs({
 *   types: ['EXAM', 'SURVEY'],
 *   categories: ['FORCE_COMPLETION']
 * });
 */
export const fetchAllAdminSignDocs = async ({types, categories, showArchived = false} = {}) => {
    const allSignDocs = await queryClient.fetchQuery({
        queryKey: queryKeys.allAdminSignDocs(showArchived),
        queryFn: () => getAllAdminSignDocsAPI({
            types: ALL_SIGN_DOC_TYPES,
            categories: ALL_SIGN_DOC_CATEGORIES,
            showArchived
        }),
    });

    return filterSignDocs({signDocs: allSignDocs, types, categories});
};

/**
 * Fetches the distinct SignDoc type/category pairs the admin has created,
 * using the React Query cache.
 *
 * Lightweight replacement for fetchAllAdminSignDocs when only the created
 * types are needed (tab filters, type dropdowns) — the server aggregates
 * instead of returning the full SignDoc list.
 *
 * @param {object} [params] - Function parameters
 * @param {boolean} [params.showArchived=false] - When true, fetches types among ARCHIVED documents (archive tab)
 * @returns {Promise<Array<{type: string, category: string, hasValidAssignment: boolean}>>}
 *          Distinct created type/category pairs; hasValidAssignment is true when
 *          at least one organization assignment of that pair is currently valid.
 */
export const fetchAdminCreatedSignDocTypes = async ({showArchived = false} = {}) => {
    return queryClient.fetchQuery({
        queryKey: queryKeys.adminCreatedSignDocTypes(showArchived),
        queryFn: () => getAdminCreatedSignDocTypesAPI({
            types: ALL_SIGN_DOC_TYPES,
            categories: ALL_SIGN_DOC_CATEGORIES,
            showArchived
        }),
    });
};

/**
 * React Query hook to fetch user's admin organizations
 *
 * This hook:
 * - Fetches organizations from backend
 * - Filters for admin permissions using getAdminOrganizations
 * - Caches results for 5 minutes (staleTime)
 * - Automatically refetches on window focus
 * - Deduplicates simultaneous requests
 *
 * @returns {object} Query result with data, isLoading, error, refetch
 * @property {Array} data - Admin organizations array (or undefined during loading)
 * @property {boolean} isLoading - True if first fetch is in progress
 * @property {boolean} isFetching - True if any fetch is in progress (including background refetch)
 * @property {boolean} isError - True if query failed
 * @property {Error} error - Error object if query failed
 * @property {function} refetch - Function to manually refetch data
 *
 * @example
 * const { data: adminOrgs, isLoading } = useAdminOrganizationsQuery();
 *
 * if (isLoading) return <LoadingSpinner />;
 *
 * return <OrganizationList organizations={adminOrgs} />;
 */
export const useAdminOrganizationsQuery = () => {
    return useQuery({
        queryKey: queryKeys.adminOrganizations(),
        queryFn: async () => {
            const orgs = await getUserOrganizationsPlain();
            return getAdminOrganizations({organizationsWithPermission: orgs});
        },
    });
};

/**
 * React Query hook to fetch unique SignDocs assigned to user's organizations
 *
 * This hook:
 * - Fetches SignDocs from backend with optional VPO filter
 * - Caches results per includePersonalOrgs parameter (separate cache entries)
 * - Automatically refetches on window focus
 * - Deduplicates simultaneous requests
 *
 * @param {object} params - Hook parameters
 * @param {boolean} [params.includePersonalOrgs=false] - Whether to include VPO organizations
 * @param {boolean} [params.enabled=true] - Whether to enable the query (for conditional fetching)
 * @returns {object} Query result with data, isLoading, error, refetch
 *
 * @example
 * // Without VPO
 * const { data: signDocs, isLoading } = useUniqueSignDocsQuery({ includePersonalOrgs: false });
 *
 * // With VPO
 * const { data: signDocs, isLoading } = useUniqueSignDocsQuery({ includePersonalOrgs: true });
 *
 * // Conditional fetch (only fetch when needed)
 * const { data: signDocs } = useUniqueSignDocsQuery({
 *   includePersonalOrgs: false,
 *   enabled: withCoursesAndSignDocs
 * });
 */
export const useUniqueSignDocsQuery = ({includePersonalOrgs = false, enabled = true} = {}) => {
    return useQuery({
        queryKey: queryKeys.uniqueSignDocs(includePersonalOrgs),
        queryFn: () => getUserUniqueSignDocsAPI({includePersonalOrgs}),
        enabled,
    });
};

/**
 * React Query hook to fetch ALL admin SignDocs with frontend filtering
 *
 * Fetches all SignDocs once and filters on frontend. Optimal for:
 * - Companies with <100 SignDocs
 * - Frequent filter changes (type/category toggling)
 * - Cases where all types/categories are eventually used
 *
 * Cache Strategy:
 * - Separate cache for showArchived=true vs showArchived=false
 * - Types and categories are filtered on frontend (not part of cache key)
 *
 * @param {object} params - Hook parameters
 * @param {Array<string>} [params.types] - SignDoc types to filter (frontend filter)
 * @param {Array<string>} [params.categories] - SignDoc categories to filter (frontend filter)
 * @param {boolean} [params.showArchived=false] - Whether to include archived SignDocs (separate cache)
 * @param {boolean} [params.enabled=true] - Whether to enable the query
 * @returns {object} Query result with filtered data
 *
 * @example
 * const { data: signDocs, isLoading } = useAllAdminSignDocsQuery({
 *   types: ['READ_AND_SIGN_DOC_PDF', 'WATCH_AND_SIGN_VIDEO_FILE'],
 *   categories: ['FORCE_COMPLETION']
 * });
 */
export const useAllAdminSignDocsQuery = ({
    types,
    categories,
    showArchived = false,
} = {}) => {
    // Fetch ALL SignDocs (only showArchived affects cache key)
    const query = useQuery({
        queryKey: queryKeys.allAdminSignDocs(showArchived),
        queryFn: () => getAllAdminSignDocsAPI({types: ALL_SIGN_DOC_TYPES, categories: ALL_SIGN_DOC_CATEGORIES, showArchived}),
    });

    // Filter on frontend using useMemo (uses shared filterSignDocs helper)
    const filteredData = useMemo(() => {
        if (!query.data) return undefined;

        const filtered = filterSignDocs({signDocs: query.data, types, categories});

        // Sort by createdAt descending (newest first)
        return orderBy(filtered, ['createdAt'], ['desc']);
    }, [query.data, types, categories]);

    return {
        ...query,
        data: filteredData,
    };
};
